import styles from "./EditCampaign.module.css";
import { getPageFromName, PAGES } from "../../route_utils";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import React, { useEffect, useContext, useState, useRef } from "react";
import SwitchWrapper from "../SwitchWrapper/SwitchWrapper";
import { useNavigate, useLocation } from "react-router-dom";
import api_endpoints from "../../api";
import { numberWithCommas } from "../../utils";
import SlIconButton from "@shoelace-style/shoelace/dist/react/icon-button";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Divider,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Alert
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import "dayjs/locale/fr-ca";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import dayjs from "dayjs";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import AuthContext from "../../context/AuthContext/AuthContext";
import CancelButton from "../../widgets/CancelButton/CancelButton";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import ImageInputV2 from "../../widgets/ImageInput/ImageInputV2";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import QRCode from "qrcode";
import { BASE_URL } from "../../api/constants";

const EditCampaign = () => {
  const authContext = useContext(AuthContext);
  const [organizationLocales, setOrganizationLocales] = useState([]);
  const [countryLocales, setCountryLocales] = useState([]);
  const [combinedLocales, setCombinedLocales] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState("");
  const [localeTranslationData, setLocaleTranslationData] = useState({});
  const [campaignTranslations, setCampaignTranslations] = useState({});
  const [translationInitialized, setTranslationInitialized] = useState(false);
  const { state } = useLocation();
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const defaultFormState = {
    kiosk_preset: [],
    is_fundraiser: false,
    is_fundraiser_accept_recurring: false,
    enabled: false,
    hide_in_kiosk: false,
    can_donate_anonymously: true,
    has_duration: false,
    has_objective: false,
    has_minimum_donation: false,
    donation_minimum: "",
    objective: "",
    name: "",
    description: "",
    date_start: null,
    date_end: null,
    location: "",
    organization: "",
  };
  const [formData, setFormData] = useState(defaultFormState);
  const [presetKioskModalOpen, setPresetKioskModalOpen] = useState(false);
  const [presetAmount, setPresetAmount] = useState("");
  const [presetAmountExists, setPresetAmountExists] = useState(false);
  const [presetAmountBelowMin, setPresetAmountBelowMin] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [messageErrorAlert, setMessageErrorAlert] = useState("");
  const [locations, setLocations] = useState([]);
  const [image, setImage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPreview, setPreviewOpen] = useState(false);
  const [placementPreview, setPlacementPreview] = useState();
  const [fundraiserUrl, setFundraiserUrl] = useState(null);
  const [fundraiserActionLoading, setFundraiserActionLoading] = useState(false);
  const [fundraiserPrintLoading, setFundraiserPrintLoading] = useState(false);
  const [fundraiserError, setFundraiserError] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const fundraiserFetchedRef = useRef(false);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { t } = useCustomTranslation("EditCampaign");
  const {
    showSuccessAlert,
    setShowSuccessAlert,
    messageSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchCampaignData();
  }, [locations]);

  useEffect(() => {
    if (countryLocales.length > 0) {
      const combined = [...countryLocales, ...organizationLocales];
      setCombinedLocales(combined);
      setSelectedLocale(countryLocales[0].id); // Assuming each locale has an 'id' property
    }
  }, [organizationLocales, countryLocales]);

  useEffect(() => {
    if (formData && selectedLocale) {
      const translationData = localeTranslationData[selectedLocale];
      if (translationData) {
        const { campaign_name, campaign_description } = translationData;
        if (!campaign_name) {
          setCampaignTranslations((prevTranslations) => ({
            ...prevTranslations,
            [selectedLocale]: {
              campaign_name: formData.name || "",
              campaign_description: formData.description || "",
            },
          }));
        } else {
          setCampaignTranslations((prevTranslations) => ({
            ...prevTranslations,
            [selectedLocale]: {
              campaign_name: campaign_name,
              campaign_description: campaign_description,
            },
          }));
        }
      } else {
        setCampaignTranslations((prevTranslations) => ({
          ...prevTranslations,
          [selectedLocale]: {
            campaign_name: formData.name || "",
            campaign_description: formData.description || "",
          },
        }));
      }
    }
  }, [translationInitialized]);

  useEffect(() => {
    const campaignId = state?.campaign_id;
    if (!campaignId) {
      return;
    }
    if (!formData?.is_fundraiser) {
      return;
    }
    if (fundraiserUrl || fundraiserActionLoading) {
      return;
    }
    if (fundraiserFetchedRef.current) {
      return;
    }

    const fetchFundraiserUrl = async () => {
      try {
        const response = await api_endpoints.getCampaignFundraiserUrl(
          campaignId
        );
        if (response.status === 200) {
          setFundraiserUrl(response.data.url);
          fundraiserFetchedRef.current = true;
          setFundraiserError("");
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404) {
          fundraiserFetchedRef.current = true;
          if (formData?.is_fundraiser) {
            setFundraiserActionLoading(true);
            try {
              await ensureFundraiserUrlExists(campaignId);
            } finally {
              setFundraiserActionLoading(false);
            }
          }
          return;
        }
        console.error(error);
        fundraiserFetchedRef.current = false;
      }
    };

    fetchFundraiserUrl();
  }, [
    formData?.is_fundraiser,
    fundraiserUrl,
    fundraiserActionLoading,
    state?.campaign_id,
  ]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await fetchLocations();
      await fetchOrganizationLocales();
      await fetchCountryLocales();
      await fetchLocaleTranslations(state.campaign_id);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setLoadingError(true);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (
      formData.has_duration &&
      (!formData.date_start ||
        !formData.date_end ||
        formData.date_start > formData.date_end)
    ) {
      setDateError(true);
      setSubmitting(false);
      return;
    }

    const data = setRequestData();
    const campaignName =
      campaignTranslations[selectedLocale]?.campaign_name || "";
    data.set("name", campaignName);

    try {
      let response = await api_endpoints.editCampaign(data);

      if (response.status === 200) {
        const translationsResponse =
          await api_endpoints.editCampaignTranslations(
            state.campaign_id,
            campaignTranslations
          );
        if (translationsResponse.status === 200) {
          setMessageSuccessAlert(response.data);
          setShowSuccessAlert(true);
          navigate(getPageFromName("Campagnes").path);
        } else {
          console.error("Failed to update translations:", translationsResponse);
        }
      }
    } catch (e) {
      console.error(e);
      setMessageErrorAlert(t("edit_campaign_error"));
      setShowErrorAlert(true);
    }

    setSubmitting(false);
  };

  const changeFormData = (key, value) => {
    setFormData((prevState) => ({ ...prevState, [key]: value }));
  };

  const addPresetAmount = () => {
    const raw = String(presetAmount).replace(",", ".").trim();
    if (!raw) return;

    const n = Number(raw);
    if (Number.isNaN(n)) return;

    const rounded = Number(n.toFixed(2));
    const current = (formData.kiosk_preset ?? []).map(Number);

    if (current.includes(rounded)) {
      setPresetAmountExists(true);
      return;
    }

    if (
      formData.has_minimum_donation &&
      formData.donation_minimum &&
      Number(formData.donation_minimum) > rounded
    ) {
      setPresetAmountBelowMin(true);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      kiosk_preset: [...current, rounded].sort((a, b) => a - b),
    }));

    setPresetKioskModalOpen(false);
    setPresetAmountBelowMin(false);
    setPresetAmount("");
  };

  const fetchCampaignData = async () => {
    try {
      let response = await api_endpoints.getCampaign(state.campaign_id);

      if (response.status === 200) {
        const data = response.data.campaign;
        const normalized = normalizeCampaignForm({
          ...data,
          location: data.location?._id || "",
        });
        setFormData(normalized);
        const fundraiserToken = data.fundraiser_url || null;
        setFundraiserUrl(fundraiserToken);
        fundraiserFetchedRef.current = Boolean(fundraiserToken);
        if (data.organization_data?.legal_name) {
          setOrganizationName(data.organization_data.legal_name);
        }
        if (normalized.is_fundraiser && !fundraiserToken) {
          setFundraiserActionLoading(true);
          try {
            await ensureFundraiserUrlExists(state.campaign_id);
          } finally {
            setFundraiserActionLoading(false);
          }
        }
      }
    } catch (e) {
      setLoadingError(true);
    }
  };

  const fetchLocations = async () => {
    setLoading(true);
    try {
      let response = await api_endpoints.getLocations(
        authContext.authContext.accessToken.organization_id,
        null
      );

      if (response.status === 200) {
        setLocations(response.data);
        await fetchCampaignData();
      }
    } catch (e) {
      console.error(e);
      setLoadingError(true);
    }
    setLoading(false);
  };

  const fetchOrganizationLocales = async () => {
    setLoading(true);
    try {
      let response = await api_endpoints.getOrganizationLocale(
        authContext.authContext.accessToken.organization_id
      );

      if (response.status === 200) {
        const selected = response.data.map((locale) => ({
          id: locale.locale._id,
          label: `${locale.locale.locale} (${locale.locale.locale_abbreviation})`,
        }));
        setOrganizationLocales(selected);
      }
    } catch (e) {
      console.error(e);
      setLoadingError(true);
    }
    setLoading(false);
  };

  const fetchCountryLocales = async () => {
    setLoading(true);

    try {
      const organizationResponse = await api_endpoints.getOrganization(
        authContext.authContext.accessToken.organization_id
      );

      if (
        organizationResponse.status === 200 &&
        organizationResponse.data.country
      ) {
        const countryId = organizationResponse.data.country._id;
        const countryLocalesResponse = await api_endpoints.getCountryLocales(
          countryId
        );

        if (countryLocalesResponse.status === 200) {
          const selected = countryLocalesResponse.data.map((locale) => ({
            id: locale.locale._id,
            label: `${locale.locale.locale} (${locale.locale.locale_abbreviation})`,
          }));

          setCountryLocales(selected);
        } else {
          console.error(
            "Failed to fetch country locales:",
            countryLocalesResponse
          );
        }
      } else {
        console.error(
          "Failed to fetch organization or country data:",
          organizationResponse
        );
      }
    } catch (error) {
      console.error("Error fetching country locales:", error);
    }

    setLoading(false);
  };

  const fetchLocaleTranslations = async (campaignId) => {
    try {
      const response = await api_endpoints.getCampaignTranslations(campaignId);
      if (response.status === 200) {
        const translations = response.data.reduce((acc, translation) => {
          acc[translation.locale_id] = {
            campaign_name: translation.campaign_name,
            campaign_description: translation.campaign_description,
          };
          return acc;
        }, {});
        setLocaleTranslationData(translations);
        setTranslationInitialized(true);
      }
    } catch (error) {
      console.error("Error fetching locale translations:", error);
    }
  };

  const compareNumbers = (a, b) => {
    return a - b;
  };

  const normalizeCampaignForm = (data = {}) => {
    const presets = Array.isArray(data.kiosk_preset)
      ? data.kiosk_preset.map((n) => Number(n)).sort(compareNumbers)
      : defaultFormState.kiosk_preset;
    const organizationId =
      data && typeof data.organization === "object" && data.organization !== null
        ? data.organization._id || ""
        : data.organization;

    return {
      ...defaultFormState,
      ...data,
      kiosk_preset: presets,
      is_fundraiser: Boolean(data.is_fundraiser),
      is_fundraiser_accept_recurring: Boolean(data.is_fundraiser_accept_recurring),
      enabled: Boolean(data.enabled),
      hide_in_kiosk: Boolean(data.hide_in_kiosk),
      can_donate_anonymously:
        data.can_donate_anonymously ?? defaultFormState.can_donate_anonymously,
      has_duration: Boolean(data.has_duration),
      has_objective: Boolean(data.has_objective),
      has_minimum_donation: Boolean(data.has_minimum_donation),
      donation_minimum:
        data.donation_minimum !== undefined && data.donation_minimum !== null
          ? String(data.donation_minimum)
          : defaultFormState.donation_minimum,
      objective:
        data.objective !== undefined && data.objective !== null
          ? String(data.objective)
          : defaultFormState.objective,
      name: data.name ?? defaultFormState.name,
      description: data.description ?? defaultFormState.description,
      date_start: normalizeCampaignDate(data.date_start),
      date_end: normalizeCampaignDate(data.date_end),
      organization:
        organizationId || data.organization_id || defaultFormState.organization,
    };
  };

  const normalizeCampaignDate = (value) => {
    if (value === null || value === undefined || value === "") return null;

    if (typeof value === "string") {
      const trimmedValue = value.trim();

      if (trimmedValue === "") return null;

      if (/^\d+$/.test(trimmedValue)) {
        return Number(trimmedValue);
      }

      return trimmedValue;
    }

    return value;
  };

  const sanitizeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const handleSelectImage = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const setRequestData = () => {
    const data = new FormData();

    if (image) data.append("file", image, image.name);

    for (const [key, value] of Object.entries(formData)) {
      if (key === "kiosk_preset") {
        const arr = Array.isArray(value)
          ? value
          : String(value).split(",").map((v) => Number(v));
        arr.forEach((v) => data.append("kiosk_preset[]", Number(v)));
      } else {
        data.append(key, value ?? "");
      }
    }

    return data;
  };

  const handleRemovePresetKiosk = (preset) => {
    setFormData((prevState) => ({
      ...prevState,
      kiosk_preset: prevState.kiosk_preset.filter((item) => item !== preset),
    }));
  };

  const handleClick = (newPlacement) => (event) => {
    setAnchorEl(event.currentTarget);
    setPreviewOpen((prev) => placementPreview !== newPlacement || !prev);
    setPlacementPreview(newPlacement);
  };

  const removeImage = () => {
    document.getElementById("imageInput").value = null;
    setImage(null);
  };

  const ensureFundraiserUrlExists = async (campaignId) => {
    if (!campaignId) {
      return;
    }
    try {
      const response = await api_endpoints.ensureCampaignFundraiserUrl(
        campaignId
      );
      if (response.status === 200 || response.status === 201) {
        const url = response.data?.publicUrl;
        if (url) {
          setFundraiserUrl(url);
          fundraiserFetchedRef.current = true;
          setFundraiserError("");
        }
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        return;
      }
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t("create_fundraiser_link_error");
      setFundraiserError(message);
      fundraiserFetchedRef.current = false;
    }
  };

  const openFundraiserPrintPreview = ({
    organization,
    campaign,
    description,
    qrImage,
    link,
    logo,
  }) => {
    const printWindow = window.open("", "", "width=1000,height=1000");
    if (!printWindow) {
      setFundraiserError(t("print_preview_error"));
      return;
    }

    printWindow.document.write(`
    <html>
      <head>
        <style>
          @page { margin: 0; size: auto; }
          body {
            font-family: Arial, sans-serif;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            box-sizing: border-box;
          }

          .content {
            flex: 1 0 auto;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            text-align: center;
            padding: 20px;
            box-sizing: border-box;
          }

          .logo { max-width: 100px; max-height: 100px; margin-bottom: 20px; }
          h2 { margin-top: 10px; margin-bottom: 8px; font-size: 26px; }
          h1 { margin: 0 0 16px; font-size: 38px; }
          .description { font-size: 18px; margin-bottom: 20px; max-width: 650px; }

          .qr-code-container {
            margin: 24px 0;
            display: flex;
            justify-content: center;
          }

          .footer {
            flex-shrink: 0;
            text-align: center;
            padding: 20px 0;
            margin-top: auto;
            box-sizing: border-box;
          }

          p { margin: 6px 0; font-size: 16px; }

          .warning {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            color: red;
            margin-top: 15px;
          }
          .warning-icon { font-size: 20px; margin-right: 8px; }

          a { color: #1976d2; word-break: break-all; }

          /* Print rules */
          @media print {
            .no-print { display: none; }
            .link-container { display: none; }
            body { padding: 0; }
            .content { padding: 20px 30px; }
          }
        </style>
      </head>

      <body>
        <div class="content">
          <div class="no-print">
            <button onclick="window.print()">${t("print_or_save_pdf")}</button>
          </div>

          ${logo
        ? `<img src="${`${process.env.REACT_APP_S3_URL}${logo}`}" alt="${t(
          "organization_logo_alt"
        )}" class="logo" />`
        : ""
      }

          <h2>${sanitizeHtml(organization)}</h2>
          <h1>${sanitizeHtml(campaign)}</h1>
          <p class="description">${sanitizeHtml(description)}</p>

          <div class="qr-code-container">
            <img src="${qrImage}" alt="${t(
        "fundraiser_qr_code_alt"
      )}" width="360" height="360" />
          </div>

          <div class="link-container">
            <p>
              <a href="${link}" target="_blank" rel="noopener noreferrer">${t(
        "click_to_open_link"
      )}</a>
            </p>
          </div>


        </div>

        <div class="footer">
          <p>${t("powered_by")}</p>
          <img src="${process.env.PUBLIC_URL
      }/Kiosk-Donations.svg" alt="Kiosk Donations" width="160" />
        </div>
      </body>
    </html>
  `);

    printWindow.document.close();
  };

  const handleFundraiserPrint = async () => {
    if (!state?.campaign_id) {
      return;
    }

    setFundraiserPrintLoading(true);
    setFundraiserError("");
    try {
      const response = await api_endpoints.getCampaignFundraiserUrl(
        state.campaign_id
      );
      const fundraiserLink = response.data.publicUrl;

      if (!fundraiserLink) {
        throw new Error("Fundraiser URL not found.");
      }

      const qrImage = await QRCode.toDataURL(fundraiserLink, { width: 360 });
      const organizationResponse = await api_endpoints.getOrganization(
        authContext.authContext.accessToken.organization_id
      );
      const logoUrl = organizationResponse.data.logo
        ? organizationResponse.data.logo
        : null;
      openFundraiserPrintPreview({
        organization: organizationName || "",
        campaign:
          campaignTranslations[selectedLocale]?.campaign_name ||
          formData?.name ||
          "",
        description:
          campaignTranslations[selectedLocale]?.campaign_description ||
          formData.description ||
          "",
        qrImage,
        link: fundraiserLink,
        logo: logoUrl,
      });
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t("generate_qr_code_error");
      setFundraiserError(message);
    } finally {
      setFundraiserPrintLoading(false);
    }
  };

  const handleFundraiserToggle = async (checked) => {
    if (fundraiserActionLoading) {
      return;
    }

    const previousValue = Boolean(formData?.is_fundraiser);
    const previousUrl = fundraiserUrl;
    changeFormData("is_fundraiser", checked);
    setFundraiserError("");

    if (!state?.campaign_id) {
      return;
    }

    setFundraiserActionLoading(true);

    try {
      if (checked) {
        const response = await api_endpoints.ensureCampaignFundraiserUrl(
          state.campaign_id
        );
        if (response.status === 200 || response.status === 201) {
          const token = response.data?.url;
          if (!token) {
            throw new Error("Missing fundraiser URL token");
          }

          setFundraiserUrl(token);
          fundraiserFetchedRef.current = true;
        }
      } else {
        await api_endpoints.deleteCampaignFundraiserUrl(state.campaign_id);
        setFundraiserUrl(null);
        fundraiserFetchedRef.current = false;
      }
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      if (!checked && status === 404) {
        setFundraiserUrl(null);
        fundraiserFetchedRef.current = false;
        setFundraiserError("");
        return;
      }
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t("update_fundraiser_link_error");
      setFundraiserError(message);
      changeFormData("is_fundraiser", previousValue);
      setFundraiserUrl(previousUrl);
      if (previousValue) {
        fundraiserFetchedRef.current = true;
      }
    } finally {
      setFundraiserActionLoading(false);
    }
  };

  const changelocale = (localeId) => {
    setSelectedLocale(localeId);
    if (localeTranslationData[localeId]) {
      const { campaign_name, campaign_description } =
        localeTranslationData[localeId];
      setFormData((prevState) => ({
        ...prevState,
        name: campaign_name || prevState.name,
        description: campaign_description || prevState.description,
      }));
      setCampaignTranslations((prevTranslations) => ({
        ...prevTranslations,
        [localeId]: {
          campaign_name:
            campaign_name || prevTranslations[localeId]?.campaign_name || "",
          campaign_description:
            campaign_description ||
            prevTranslations[localeId]?.campaign_description ||
            "",
        },
      }));
    }
  };

  return (
    <LoadingContainer loading={loading} error={loadingError}>
      <Dialog
        fullWidth
        open={presetKioskModalOpen}
        onClose={() => {
          setPresetKioskModalOpen(false);
          setPresetAmountExists(false);
        }}
        style={{ "--width": "30vw" }}
      >
        <DialogTitle>{t("create_campaign_addCash_dialogue_title")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus={true}
            fullWidth
            value={presetAmount}
            onInput={(e) => {
              setPresetAmount(e.target.value);
              setPresetAmountExists(false);
            }}
            type="number"
            placeholder={t("create_campaign_amount")}
            label={t("create_campaign_amount")}
            FormHelperTextProps={{ sx: { color: "error.main" } }}
            helperText={
              presetAmountExists
                ? t("create_campaign_alread_exists_amount")
                : presetAmountBelowMin
                  ? t("create_campaign_lessThanMinAmount_text")
                  : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={addPresetAmount}>
            {t("add_text")}
          </Button>
        </DialogActions>
      </Dialog>
      <form
        id="publish_campaign_form"
        onSubmit={handleSubmit}
        autoComplete="off"
        encType="multipart/form-data"
      >
        <div className="container-white flex-col space-y-4 mt-4">
          {/* Hidden Print Preview Component (for printing only) */}

          <div className="flex flex-1 flex-col space-y-4">
            <div>
              <FormControl fullWidth>
                <InputLabel required>
                  {" "}
                  {t("create_campaign_location_choice")}
                </InputLabel>
                <Select
                  value={formData?.location}
                  label={t("create_campaign_location_choice")}
                  onChange={(e) => changeFormData("location", e.target.value)}
                  fullWidth
                  required
                >
                  {locations.map((location) => (
                    <MenuItem key={location._id} value={location._id}>
                      <div className="flex flex-col">
                        <label className="text-lg font-semibold">
                          {location.location_name}
                        </label>
                        <label>{location.address}</label>
                        <label>{location.phone}</label>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <ImageInputV2
                defaultImage={formData.image_url}
                image={image}
                onChange={handleSelectImage}
                removeImage={removeImage}
                removeDefaultImage={() => changeFormData("image_url", "")}
                placeholder={t("create_campaign_imagepreview")}
                helperText={t("create_campaign_imagerequirements")}
              />
            </div>
            <Divider />
            <div className="flex flex-1 flex-col space-y-4">
              <div>
                <FormControl fullWidth>
                  <InputLabel required>
                    {t("create_campaign_language_choice")}
                  </InputLabel>
                  <Select
                    value={selectedLocale}
                    label={t("create_campaign_language_choice")}
                    onChange={(e) => changelocale(e.target.value)}
                    fullWidth
                    required
                  >
                    {combinedLocales.map((locale) => (
                      <MenuItem key={locale.id} value={locale.id}>
                        <div className="flex flex-col">{locale.label}</div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className="flex items-end space-x-4">
              <TextField
                fullWidth
                placeholder={t("create_campaign_name")}
                label={t("create_campaign_name")}
                required
                value={
                  campaignTranslations[selectedLocale]?.campaign_name || ""
                }
                onChange={(e) => {
                  // Update the translation for the selected locale
                  setCampaignTranslations((prevTranslations) => ({
                    ...prevTranslations,
                    [selectedLocale]: {
                      ...prevTranslations[selectedLocale],
                      campaign_name: e.target.value,
                    },
                  }));
                }}
              />
              <SwitchWrapper
                label={t("create_campaign_duration")}
                style={{ width: "100%" }}
                className="space-y-2"
                checked={formData.has_duration}
                onChange={(e) => {
                  const checked = e.target.checked;
                  changeFormData("has_duration", checked);

                  if (!checked) {
                    changeFormData("date_start", null);
                    changeFormData("date_end", null);
                  }
                }}
                required={formData.has_duration}
              >
                <div className="flex space-x-2 justify-between w-full">
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="fr-ca"
                  >
                    <DatePicker
                      value={
                        formData.date_start ? dayjs(formData.date_start) : null
                      }
                      onChange={(value) =>
                        changeFormData(
                          "date_start",
                          value ? value.toDate().getTime() : null
                        )
                      }
                      label={t("create_campaign_start")}
                      sx={{ width: "100%" }}
                      slotProps={
                        dateError
                          ? {
                            textField: {
                              error: dateError,
                              helperText: "Dates invalides",
                              required: formData.has_duration,
                            },
                          }
                          : {
                            textField: {
                              error: false,
                              required: formData.has_duration,
                            },
                          }
                      }
                    />
                    <DatePicker
                      value={
                        formData.date_end ? dayjs(formData.date_end) : null
                      }
                      onChange={(value) =>
                        changeFormData(
                          "date_end",
                          value ? value.toDate().getTime() : null
                        )
                      }
                      label={t("create_campaign_end")}
                      sx={{ width: "100%" }}
                      slotProps={
                        dateError
                          ? {
                            textField: {
                              error: dateError,
                              helperText: "Dates invalides",
                              required: formData.has_duration,
                            },
                          }
                          : {
                            textField: {
                              error: false,
                              required: formData.has_duration,
                            },
                          }
                      }
                    />
                  </LocalizationProvider>
                </div>
              </SwitchWrapper>
            </div>
            <div className="flex flex-row space-x-4">
              <SwitchWrapper
                label={t("create_campaign_objective")}
                style={{ width: "100%" }}
                checked={formData.has_objective}
                onChange={(e) => {
                  changeFormData("has_objective", e.target.checked);
                }}
                required={formData.has_objective}
              >
                <TextField
                  label={t("create_campaign_objectivetext")}
                  type="number"
                  fullWidth
                  value={formData.objective}
                  onInput={(e) => {
                    changeFormData("objective", e.target.value);
                  }}
                  required={formData.has_objective}
                />
              </SwitchWrapper>
              <SwitchWrapper
                style={{ marginLeft: 30, width: "100%" }}
                label={t("create_campaign_minamount")}
                checked={formData.has_minimum_donation}
                onChange={(e) => {
                  changeFormData("has_minimum_donation", e.target.checked);
                }}
                required={formData.has_minimum_donation}
              >
                <TextField
                  label={t("create_campaign_no_min")}
                  fullWidth
                  type="number"
                  value={formData.donation_minimum}
                  onInput={(e) => {
                    changeFormData("donation_minimum", e.target.value);
                  }}
                  required={formData.has_minimum_donation}
                />
              </SwitchWrapper>
            </div>
            <div>
              <TextField
                placeholder={t("create_campaign_description")}
                label={t("create_campaign_description")}
                fullWidth
                multiline
                rows={5}
                value={
                  campaignTranslations[selectedLocale]?.campaign_description ||
                  ""
                }
                onChange={(e) => {
                  setCampaignTranslations((prevTranslations) => ({
                    ...prevTranslations,
                    [selectedLocale]: {
                      ...prevTranslations[selectedLocale],
                      campaign_description: e.target.value,
                    },
                  }));
                }}
              />
            </div>
          </div>
          <Divider />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.enabled)}
                  onChange={(e) => changeFormData("enabled", e.target.checked)}
                />
              }
              label={t("create_campaign_active")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.hide_in_kiosk)}
                  onChange={(e) => changeFormData("hide_in_kiosk", e.target.checked)}
                />
              }
              label={t("hide_in_kiosk")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.can_donate_anonymously)}
                  onChange={(e) =>
                    changeFormData("can_donate_anonymously", e.target.checked)
                  }
                />
              }
              label={t("create_campaign_anonymous_donations")}
            />
            {/* {authContext.authContext.accessToken.organization_id ===
            "65f34e519569270c8bc0be4b" ? ( */}
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.is_fundraiser)}
                    disabled={fundraiserActionLoading}
                    onChange={(e) => handleFundraiserToggle(e.target.checked)}
                  />
                }
                label={t("is_fundraiser")}
              />
              <Tooltip title={t("info_qr_code")}>
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
              {formData.is_fundraiser ? (
                <div className="mt-2 flex flex-col gap-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formData.is_fundraiser_accept_recurring)}
                        onChange={(e) =>
                          changeFormData("is_fundraiser_accept_recurring", e.target.checked)
                        }
                      />
                    }
                    label={t("is_fundraiser_accept_recurring")}
                  />
                  <ButtonLoading
                    loading={fundraiserPrintLoading}
                    variant="outlined"
                    type="button"
                    disabled={fundraiserActionLoading}
                    onClick={handleFundraiserPrint}
                  >
                    {t("print_fundraiser_qr_button")}
                  </ButtonLoading>
                  {!fundraiserUrl && !fundraiserActionLoading ? (
                    <span className="text-sm text-gray-500">
                      Fundraiser link unavailable.
                    </span>
                  ) : null}
                </div>
              ) : null}
              {fundraiserError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {fundraiserError}
                </Alert>
              ) : null}
            </div>
            {/* ) : (
              <></>
            )} */}
          </FormGroup>
          <Divider />
          <div className={styles.amount_presets_container}>
            <label>{t("create_campaign_preset_donations")}</label>
            <div className="gap-4 flex items-center">
              {formData.kiosk_preset?.map((preset, index) =>
                preset ? (
                  <div className={styles.kiosk_preset} key={preset + index}>
                    <SlIconButton
                      className="flex-[0] !justify-end z-10 text-red-500"
                      name="trash"
                      label="Remove preset"
                      onClick={() => handleRemovePresetKiosk(preset)}
                    />
                    <div className={styles.preset_amount}>
                      <SlIcon library="boxicons" name="bx-dollar" />
                      {numberWithCommas(parseFloat(preset).toFixed(2))}
                    </div>
                  </div>
                ) : (
                  <React.Fragment key={preset + index}></React.Fragment>
                )
              )}
              <Button
                variant="contained"
                onClick={() => setPresetKioskModalOpen(true)}
                className="flex flex-col text-center w-[123px] h-[110px]"
                sx={{
                  padding: 7,
                }}
              >
                {t("create_campaign_addamount")}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4 space-x-4 flex pb-4 justify-end">
          <CancelButton
            disabled={submitting}
            label={t("create_campaign_cancel_button")}
          />
          <ButtonLoading loading={submitting} variant="contained" type="submit">
            {t("edit_campaign_button")}
          </ButtonLoading>
        </div>
        {showErrorAlert && (
          <Alert severity="error" onClose={() => setShowErrorAlert(false)}>
            {messageErrorAlert}
          </Alert>
        )}
      </form>
    </LoadingContainer>
  );
};

export default EditCampaign;
